import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import api from 'api';
import { setAlert } from 'redux/alert';
import type { RootState } from 'redux/store';
import type { OptionInterface } from 'types';
import { generateOptionFromObjectArray } from 'utils/dataFormatter';

/** Shared across organization tabs so remounts do not each hit the list endpoint. */
export const CLIENT_SERVICE_PROVIDER_STALE_MS = 5 * 60 * 1000;

export const clientServiceProviderKeys = {
    all: ['clientServiceProviders'] as const,
    list: (userId: number, excludeOwnServiceProvider: boolean) =>
        ['clientServiceProviders', 'list', userId, excludeOwnServiceProvider] as const,
};

export const clientServiceProviderListQuery = (
    userId: number,
    excludeOwnServiceProvider: boolean
) => ({
    queryKey: clientServiceProviderKeys.list(userId, excludeOwnServiceProvider),
    queryFn: async (): Promise<OptionInterface[]> => {
        const response = await api.clientServiceProviders.getClientServiceProviders(
            undefined,
            excludeOwnServiceProvider
        );
        return generateOptionFromObjectArray(
            response.data,
            'company_name',
            'id'
        ) as OptionInterface[];
    },
    staleTime: CLIENT_SERVICE_PROVIDER_STALE_MS,
    retry: false,
});

/**
 * One toast per failed fetch. OrganizationSelector remounts on each organization
 * tab, and a cached error would otherwise alert again without a new request.
 */
let lastReportedServiceProviderErrorAt = 0;

export interface UseClientServiceProvidersOptions {
    enabled?: boolean;
    excludeOwnServiceProvider?: boolean;
}

/**
 * GET /client-service-providers for the signed-in user.
 * OrganizationSelector is mounted separately on profile, documents, agents, and
 * agreements. Those mounts share this cache for five minutes.
 */
export const useClientServiceProviders = (options?: UseClientServiceProvidersOptions) => {
    const dispatch = useDispatch();
    const userId = useSelector((state: RootState) => state.user.entity.id);
    const excludeOwnServiceProvider = options?.excludeOwnServiceProvider ?? false;
    const normalizedUserId = userId > 0 ? userId : 0;

    const query = useQuery({
        ...clientServiceProviderListQuery(normalizedUserId, excludeOwnServiceProvider),
        enabled: (options?.enabled ?? true) && normalizedUserId > 0,
    });
    const { errorUpdatedAt, isError, isFetching } = query;

    useEffect(() => {
        if (!isError || isFetching || errorUpdatedAt === 0) {
            return;
        }
        if (lastReportedServiceProviderErrorAt === errorUpdatedAt) {
            return;
        }
        lastReportedServiceProviderErrorAt = errorUpdatedAt;
        dispatch(setAlert({ message: 'somethingWentWrong', type: 'error' }));
    }, [dispatch, errorUpdatedAt, isError, isFetching]);

    return query;
};
